import { differenceInMinutes, format, isSameYear, parseISO } from 'date-fns';
import { ChevronRight, Video } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/shadcn/utils';
import type { components } from '@/types/apiSchema';
import { getInitials } from '@/utils/getInitials';

export type Interview = components['schemas']['InterviewResponse'];

interface InterviewRowProps {
  interview: Interview;
  isUpcoming: boolean;
  tenantSearch: string;
}

const formatDuration = (startsAt: string, endsAt: string) => {
  const minutes = differenceInMinutes(parseISO(endsAt), parseISO(startsAt));
  return minutes > 0 ? `${minutes} min` : undefined;
};

/**
 * One 48px row: when | who and for which job | duration | join. The
 * candidate link is stretched over the row; "Join" stays a separate anchor
 * above it (a link cannot nest a link), only on upcoming rows. Below `lg` the
 * Join anchor collapses to its icon so the action stays reachable on mobile.
 * Past rows are dimmed with explicit AA-safe colours rather than container
 * opacity, which would push the already-muted text below contrast.
 */
export function InterviewRow({
  interview,
  isUpcoming,
  tenantSearch,
}: InterviewRowProps) {
  const startsAt = parseISO(interview.meeting.startsAt);
  const candidateName =
    `${interview.candidate.firstName} ${interview.candidate.lastName}`.trim();
  const duration = formatDuration(
    interview.meeting.startsAt,
    interview.meeting.endsAt,
  );
  const joinLink = isUpcoming ? interview.meeting.googleMeetLink : null;

  return (
    <li className="px-2">
      <div
        className={cn(
          'group hover:bg-accent relative -mx-2 grid min-h-12 grid-cols-[72px_24px_minmax(0,1fr)_28px_16px] items-center gap-3 rounded-md px-2 py-1.5 transition-colors duration-100 md:grid-cols-[72px_24px_minmax(0,1fr)_minmax(0,1fr)_72px_28px_16px] lg:grid-cols-[72px_24px_minmax(0,1fr)_minmax(0,1fr)_72px_96px_16px]',
        )}
      >
        <span
          className={cn(
            'flex flex-col gap-px text-[13px] font-medium tabular-nums',
            !isUpcoming && 'text-muted-foreground',
          )}
        >
          <time dateTime={interview.meeting.startsAt}>
            {isUpcoming ? (
              format(startsAt, 'HH:mm')
            ) : (
              <>
                {format(
                  startsAt,
                  isSameYear(startsAt, new Date()) ? 'MMM d' : 'MMM d, yyyy',
                )}
                <span className="block font-normal text-neutral-500">
                  {format(startsAt, 'HH:mm')}
                </span>
              </>
            )}
          </time>
        </span>
        <span
          className={cn(
            'grid size-6 place-items-center rounded-full text-[10px] font-semibold tracking-wide',
            isUpcoming
              ? 'bg-secondary-150 text-primary-700'
              : 'bg-muted text-muted-foreground',
          )}
        >
          {getInitials(candidateName)}
        </span>
        <span className="flex min-w-0 flex-col gap-px">
          <Link
            className={cn(
              'focus-visible:ring-ring/50 truncate font-medium outline-none after:absolute after:inset-0 after:rounded-md focus-visible:after:ring-[3px]',
              !isUpcoming &&
                'text-muted-foreground group-hover:text-foreground',
            )}
            to={`/hm/jobs/${interview.job.id}/candidates/${interview.candidate.candidateId}${tenantSearch}`}
          >
            {candidateName}
          </Link>
          <span className="truncate text-[13px] text-neutral-500 md:hidden">
            {interview.job.title}
          </span>
          {interview.meeting.note && (
            <span className="truncate text-[13px] text-neutral-500 md:hidden">
              {interview.meeting.note}
            </span>
          )}
        </span>
        <span className="hidden min-w-0 flex-col gap-px md:flex">
          <span className="text-muted-foreground truncate text-[13px]">
            {interview.job.title}
          </span>
          <span className="truncate text-[13px] text-neutral-500">
            {interview.meeting.name}
          </span>
          {interview.meeting.note && (
            <span
              className="truncate text-[13px] text-neutral-500"
              title={interview.meeting.note}
            >
              {interview.meeting.note}
            </span>
          )}
        </span>
        <span className="hidden text-[13px] whitespace-nowrap text-neutral-500 tabular-nums md:block">
          {duration}
        </span>
        <span className="flex">
          {joinLink && (
            <a
              aria-label={`Join the interview with ${candidateName}`}
              className="border-input group-hover:bg-background focus-visible:ring-ring/50 relative z-10 inline-flex size-7 items-center justify-center gap-1.5 rounded-md border text-xs font-medium transition-colors outline-none focus-visible:ring-[3px] lg:h-7 lg:w-auto lg:px-2"
              href={joinLink}
              rel="noopener noreferrer"
              target="_blank"
            >
              <Video className="size-3.5 text-neutral-500" />
              <span className="hidden lg:inline">Join</span>
            </a>
          )}
        </span>
        <ChevronRight
          aria-hidden
          className="group-hover:text-muted-foreground size-4 justify-self-end text-neutral-300 transition-all duration-100 group-hover:translate-x-0.5"
        />
      </div>
    </li>
  );
}
